import { gql, type TypedDocumentNode } from '@apollo/client';
import { useIntl } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconSettings } from '@sorare/core/src/atoms/icons/system/iconSettings.ts';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { BackButton } from '@sorare/core/src/components/dialog/BackButton/index.tsx';
import { SETTINGS_NOTIFICATIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { InboxTasks } from '#src/components/inbox/InboxTasks/index.tsx';

import type {
  InboxDrawerQuery,
  InboxDrawerQueryVariables,
} from './__generated__/index.graphql.ts';

const INBOX_DRAWER_QUERY = gql`
  query InboxDrawerQuery($sport: Sport!) {
    currentUser {
      slug
      unclaimedInboxTasksCount(sport: $sport)
      ...InboxTasks_currentUser
    }
    cardSet {
      id
      currentSet(sport: $sport) {
        id
        slug
        ...InboxTasks_cardSet
      }
    }
  }
  ${InboxTasks.fragments.currentUser}
  ${InboxTasks.fragments.cardSet}
` as TypedDocumentNode<InboxDrawerQuery, InboxDrawerQueryVariables>;

const drawer = css`
  background: var(--c-surface-background);
  @media ${laptopAndAbove} {
    width: 440px;
    border-left: 1px solid var(--c-ui-primary);
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const InboxDrawer = ({ open, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const { sport } = useSportContext();

  const { data, refetch } = useQuery(INBOX_DRAWER_QUERY, {
    skip: !open,
    variables: { sport },
  });

  return (
    <Drawer
      open={open}
      onBackdropClick={onClose}
      onClose={onClose}
      className={drawer}
    >
      <SBHorizontal className="p-2">
        <BackButton onBack={onClose} />
        <Button
          size="medium"
          color="tertiary"
          aria-label={formatMessage(navLabels.settings)}
          startIcon={<FontAwesomeIcon icon={iconSettings} />}
          to={SETTINGS_NOTIFICATIONS}
        />
      </SBHorizontal>
      <div className="overflow-auto px-2 pb-2">
        {!!data && (
          <InboxTasks
            currentUser={data.currentUser}
            cardSet={data.cardSet?.currentSet}
            refetch={refetch}
          />
        )}
      </div>
    </Drawer>
  );
};
